import { getPreparePricesConfigParam } from '../../../utils/reducers/getters'
import { CalculateParamsTy } from '../../../baseComponents/Displays/DisplayPrice/types'
import DisplayPrice from '../../../baseComponents/Displays/DisplayPrice'
import { CustomRenderString, CustomRenderBoolean } from '../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { RectangularInputFormatNumberSmall, RectangularInput } from '../../../forms/Input'
import { getSeparatorsOfFormatedNumber, replaceDotsForDecimalSeparator } from '../../../utils/globals/numbers'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import { displayUnitsIcon } from '../../Price/DisplayPrice'
import { useCallback } from 'react'
import { twinPreparePrice } from '../../../utils/globals/price'
import { RowData } from '../../../baseComponents/TwinTable/types'
import { SpecificRulesProduct } from '@teinor/erp/prices/types'
import TableWithoutVirtualListing, { TableWithoutDefaultRowRenderer } from '../../../baseComponents/TwinTable/TableWithoutVirtualListing'
import { TableWithoutVirtualListingRowProps } from '../../../baseComponents/TwinTable/TableWithoutVirtualListing/types'
import { DataPricesTy, SetDataPricesTy } from '../../../utils/hooks/product/useModalCreateProductSpecificPricesLogic'


interface NewProductSpecificPricesTableProps extends NewProductSpecificPricesTableLogicProps {
    dataPrices: DataPricesTy
}

const NewProductSpecificPricesTable: React.FC<NewProductSpecificPricesTableProps> = ({ dataPrices, setDataPrices }) => {
    const { changePrice, changeIsPack, changeUnits, addUnits, deleteUnits } = useNewProductSpecificPricesTableLogic({ setDataPrices })
    const calculateParamsDefault = getPreparePricesConfigParam() as CalculateParamsTy
    const { t } = useTwinTranslation()
    const columns: ColumnTableSchema<SpecificRulesProduct> = [
        {
            id: 'id',
            dataKey: 'id',
            label: t('id', 'ID'),
            customRender: (parameterValue) => <CustomRenderString value={'T' + parameterValue} />,
            width: 60
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre')
        },
        {
            id: 'units',
            dataKey: 'units',
            label: t('fromU', 'A partir de...'),
            toolTip: t('explanationUnitsPrice', 'Es la cantidad a partir de la cual se aplica un precio. En caso de marcar que es un pack, es el conjunto de unidades que componen el pack.'),
            customRender: (parameterValue, allRowData) => <RectangularInputFormatNumberSmall value={parameterValue ?? 1} onChange={(value) => changeUnits(value, allRowData.rowIndex)} className='w-80' />,
            width: 120,
        },
        {
            id: 'is_pack',
            dataKey: 'is_pack',
            label: t('pack', 'Pack'),
            toolTip: t('explanationPack', 'El pack es un conjunto de unidades que se venden de forma conjunta. Si está marcado, el precio se aplicará sobre el conjunto de unidades.'),
            width: 80,
            customRender: (parameterValue, allRowData) =><CustomRenderBoolean onClick={() => changeIsPack(allRowData.rowIndex)}  value={String(parameterValue) === 'true'} width={50} />,
        },
        {
            id: 'price',
            dataKey: 'price',
            label: t('price', 'Precio'),
            toolTip: t('explanationPriceOrFormula', 'Es el precio o la fórmula por unidad. En el caso de que se trate de un pack, el precio es sobre el conjunto de unidades.'),
            customRender: (parameterValue, allRowData) => <RectangularInput value={replaceDotsForDecimalSeparator(parameterValue ?? allRowData.formula ?? '')} onChange={(value) => changePrice(value, allRowData.rowIndex)} />
        },
        {
            id: 'calculated_price',
            dataKey: 'calculated_price',
            label: t('finalUPrice', 'Precio u. final'),
            customRender: (_parameterValue, allRowData) => {
                return <DisplayPrice value={allRowData?.calculated_price} CurrencyId={allRowData?.CurrencyId || ''} with_vat={allRowData?.with_vat} calculateParams={{
                    decimals: allRowData?.decimals,
                    rounding: allRowData?.rounding,
                    roundingPriceType: calculateParamsDefault?.roundingPriceType
                }} />
            },
            minWidth: 180
        },
        {
            id: 'delete',
            dataKey: 'id',
            label: '',
            simpleLabel: t('deleteLine', 'Eliminar línea'),
            width: 50,
            customRender: (_parameterValue, allRowData) => displayUnitsIcon(allRowData, addUnits, deleteUnits),
        }
    ]
    const displayEditVariantPricesRowData = (props: TableWithoutVirtualListingRowProps) => {
        let className = props.className
        if (props?.rowData?.tag?.includes('[')) {
            className += ' bg-gray-F7'
        }
        if (props.rowData.deleted) {
            className += ' bg-red-FF'
        }
        return <TableWithoutDefaultRowRenderer {...props} className={className} />
    }
    return (
        <div className='flex-auto'>
            <TableWithoutVirtualListing className='table_small_padding' data={dataPrices} columns={columns} disableScroll={true} rowRenderer={displayEditVariantPricesRowData} />
        </div>
    )
}


interface NewProductSpecificPricesTableLogicProps {
    setDataPrices: SetDataPricesTy
}
const useNewProductSpecificPricesTableLogic = ({ setDataPrices }: NewProductSpecificPricesTableLogicProps) => {
    
    const changePrice = useCallback((price: string | null, rowIndex: number) => {
        setDataPrices((oldData) => {
            if (!oldData) {
                return oldData
            }
            const { decimalSeparator } = getSeparatorsOfFormatedNumber()
            let newValue = ''
            if (price) {
                newValue = price.replace(decimalSeparator, '.')
            }
            const newDataCopy = JSON.parse(JSON.stringify(oldData))
            newDataCopy[rowIndex] = {
                ...newDataCopy[rowIndex],
                'price': price === null ? null : newValue
            }
            return twinPreparePrice([...newDataCopy])
        })
    }, [setDataPrices])

    const changeIsPack = useCallback((rowIndex: number) => {
        setDataPrices((oldData) => {
            if (!oldData) {
                return oldData
            }
            const newDataCopy = JSON.parse(JSON.stringify(oldData))
            newDataCopy[rowIndex] = {
                ...newDataCopy[rowIndex],
                'is_pack': !newDataCopy[rowIndex].is_pack
            }
            return [...newDataCopy]
        })
    }, [setDataPrices])

    const changeUnits = useCallback((units: string, rowIndex: number) => {
        setDataPrices((oldData) => {
            if (!oldData) {
                return oldData
            }
            const newDataCopy = JSON.parse(JSON.stringify(oldData))
            if (!newDataCopy[rowIndex].old_units) {
                newDataCopy[rowIndex].old_units = newDataCopy[rowIndex].units || 1
            }
            newDataCopy[rowIndex] = { ...newDataCopy[rowIndex], 'units': units }
            return twinPreparePrice([...newDataCopy])
        })
    }, [setDataPrices])

    const deleteUnits = useCallback((rowIndex: number) => {
        setDataPrices((oldData) => {
            if (!oldData) {
                return oldData
            }
            const newDataCopy = JSON.parse(JSON.stringify(oldData))
            const myData = newDataCopy[rowIndex]
            newDataCopy[rowIndex] = { ...myData, deleted: !myData.deleted }
            return twinPreparePrice([...newDataCopy])
        })
    }, [setDataPrices])

    const addUnits = useCallback((rowIndex: number) => {
        setDataPrices((oldData) => {
            if (!oldData) {
                return oldData
            }
            const newDataCopy = JSON.parse(JSON.stringify(oldData))
            const myData = newDataCopy[rowIndex]
            let last = rowIndex
            for (const key in newDataCopy) {
                const element = newDataCopy[key]
                const units = parseFloat(element.units || 1)
                if (element.tag.includes(myData.tag)) {
                    if (parseFloat(newDataCopy[last].units) < units) {
                        last = parseInt(key)
                    }
                }
            }
            newDataCopy.splice(last + 1, 0, { ...myData, units: (parseFloat(newDataCopy[last].units) || 1) + 1, old_units: -1 })
            return twinPreparePrice([...newDataCopy])
        })
    }, [setDataPrices])

    const syncWithParent = useCallback(async (allRowData: RowData) => {
        changePrice(null, allRowData.rowIndex)
    }, [changePrice])

    return { changePrice, syncWithParent, changeIsPack, changeUnits, deleteUnits, addUnits }
}

export default NewProductSpecificPricesTable